import React, { useEffect, useMemo, useState } from "react";
import { vesselServices } from "../services/vessels.services";
import { useTranslation } from "react-i18next";
import { Button, Form } from "react-bootstrap";
import AssociateUserVessel from "./AssociateUserVessel";
import { Loader } from "../../_components";
import classes from "../assets/css/vessels.module.css";
import AssociatedUsersList from "./AssociatedUsersList";
import { useDispatch, useSelector } from "react-redux";
import { alertActions } from "../../_actions";
import { alertConstants } from "../../_constants";
import { decrypt, encrypt, history } from "../../_helpers";
import * as Yup from "yup";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";

export default function EditVessel({ id, associate }) {
  const [vessel, setVessel] = useState({});
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector((state) => state.authentication.user);
  const privateKey = sessionStorage.getItem("privateKey");
  const initialValues = useMemo(
    () => ({
      sheetName: "",
      vesselName: "",
      vesselIMO: "",
      vesselRegistryPort: "",
      vesselFlag: "",
      vesselType: "",
      vesselLOA: "",
      vesselMaxPassengersWithStaterooms: "",
      vesselDayPassengersOnBoard: "",
      vesselServiceProvider: "",
      vesselServiceProviderAddress: "",
      vesselServiceProviderCompany: "",
      vesselServiceProviderInsuranceRef: "",
      vesselServiceProviderVAT: "",
      vesselCentralAgent: user?.company?.name || "",
      vesselCentralAgentAddress: user?.company?.address1 || "",
      vesselCentralAgentCompany: user?.company?.registrationNumber || "",
      vesselCentralAgentInsuranceRef: user?.company?.insurance || "",
      vesselCentralAgentVAT: user?.company?.VATnumber || "",
      vesselCentralAgentMYBA: user?.company?.MYBAid || "",
      vesselStakeholder: "",
      vesselStakeholderAddress: "",
      vesselStakeholderCompany: "",
      vesselStakeholderInsuranceRef: "",
      vesselStakeholderMYBA: "",
      vesselStakeholderVAT: "",
      vesselStakeholderFinancialGuarantee: "",
      vesselSpecialConditions: "",
    }),
    [user]
  );
  const validationSchema = Yup.object({
    sheetName: Yup.string()
      .max(40, "Sheet name must be at most 80 characters")
      .required("Sheet name is required"),
    vesselName: Yup.string()
      .max(30, "Vessel name must be at most 30 characters")
      .required("Vessel name is required"),
    vesselIMO: Yup.string()
      .max(20, "IMO number must be at most 20 characters")
      .required("IMO number is required"),
    vesselRegistryPort: Yup.string()
      .max(30, "Port of registry must be at most 30 characters")
      .required("Port of registry is required"),
    vesselFlag: Yup.string()
      .max(20, "Flag must be at most 20 characters")
      .required("Flag is required"),
    vesselType: Yup.string()
      .oneOf(["M/Y", "S/Y"], "Invalid type")
      .required("Type is required"),
    vesselLOA: Yup.number()
      .required("Length overall is required")
      .positive("Length overall must be positive"),
    vesselMaxPassengersWithStaterooms: Yup.number()
      .required("Maximum passengers with staterooms is required")
      .positive("Value must be positive"),
    vesselDayPassengersOnBoard: Yup.number()
      .required("Day passengers on board is required")
      .positive("Value must be positive"),
    vesselServiceProvider: Yup.string()
      .max(60, "Service provider must be at most 60 characters")
      .required("Service provider is required"),
    vesselServiceProviderAddress: Yup.string()
      .max(150, "Service provider address must be at most 150 characters")
      .required("Service provider address is required"),
    vesselServiceProviderCompany: Yup.string()
      .max(40, "Service provider Company ID must be at most 40 characters")
      .required("Service provider Company ID is required"),
    vesselServiceProviderInsuranceRef: Yup.string()
      .max(
        30,
        "Service provider insurance reference must be at most 30 characters"
      )
      .required("Service provider insurance reference is required"),
    vesselServiceProviderVAT: Yup.string()
      .max(20, "Service provider VAT number must be at most 20 characters")
      .required("Service provider VAT number is required"),
  });
  useEffect(() => {
    const fetchAndDecryptVessel = async () => {
      setIsLoading(true);
      const publicKey = user?.publicKey;

      if (publicKey && privateKey) {
        try {
          const response = await vesselServices.getVesselById(id);
          const decryptPublicKey = response?.vessel?.isAssociated
            ? response?.vessel?.vesselPublicKey
            : publicKey;
          const decryptSecretKey = response?.vessel?.isAssociated
            ? response?.vessel?.vesselSecretKey
            : privateKey;

          const vessel = {
            sheetName: decrypt(
              response?.vessel?.sheetName,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselName: decrypt(
              response?.vessel?.vesselName,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselIMO: decrypt(
              response?.vessel?.vesselIMO,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselRegistryPort: decrypt(
              response?.vessel?.vesselRegistryPort,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselFlag: decrypt(
              response?.vessel?.vesselFlag,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselType: decrypt(
              response?.vessel?.vesselType,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselLOA: decrypt(
              `${response?.vessel?.vesselLOA}`,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselMaxPassengersWithStaterooms: decrypt(
              `${response?.vessel?.vesselMaxPassengersWithStaterooms}`,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselDayPassengersOnBoard: decrypt(
              `${response?.vessel?.vesselDayPassengersOnBoard}`,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselServiceProvider: decrypt(
              response?.vessel?.vesselServiceProvider,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselServiceProviderAddress: decrypt(
              response?.vessel?.vesselServiceProviderAddress,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselServiceProviderCompany: decrypt(
              `${response?.vessel?.vesselServiceProviderCompany}`,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselServiceProviderInsuranceRef: decrypt(
              response?.vessel?.vesselServiceProviderInsuranceRef,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselServiceProviderVAT: decrypt(
              response?.vessel?.vesselServiceProviderVAT,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselCentralAgent: decrypt(
              response?.vessel?.vesselCentralAgent,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselCentralAgentAddress: decrypt(
              response?.vessel?.vesselCentralAgentAddress,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselCentralAgentCompany: decrypt(
              `${response?.vessel?.vesselCentralAgentCompany}`,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselCentralAgentInsuranceRef: decrypt(
              response?.vessel?.vesselCentralAgentInsuranceRef,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselCentralAgentVAT: decrypt(
              response?.vessel?.vesselCentralAgentVAT,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselCentralAgentMYBA: decrypt(
              response?.vessel?.vesselCentralAgentMYBA,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholder: decrypt(
              response?.vessel?.vesselStakeholder,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholderAddress: decrypt(
              response?.vessel?.vesselStakeholderAddress,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholderCompany: decrypt(
              `${response?.vessel?.vesselStakeholderCompany}`,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholderInsuranceRef: decrypt(
              response?.vessel?.vesselStakeholderInsuranceRef,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholderMYBA: decrypt(
              response?.vessel?.vesselStakeholderMYBA,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholderVAT: decrypt(
              response?.vessel?.vesselStakeholderVAT,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselStakeholderFinancialGuarantee: decrypt(
              response?.vessel?.vesselStakeholderFinancialGuarantee,
              decryptPublicKey,
              decryptSecretKey
            ),
            vesselSpecialConditions: decrypt(
              response?.vessel?.vesselSpecialConditions,
              decryptPublicKey,
              decryptSecretKey
            ),
            isAssociated: response?.vessel?.isAssociated,
            vesselPublicKey: response?.vessel?.vesselPublicKey,
            vesselSecretKey: response?.vessel?.vesselSecretKey,
          };

          setVessel(vessel);
        } catch (error) {
          if (!isLoading)
            dispatch(alertActions.setAlert(t(error), alertConstants.ERROR));
        }
      }

      setIsLoading(false);
    };

    fetchAndDecryptVessel();
  }, [id, user, privateKey]);

  const [showAssociateModal, setShowAssociateModal] = useState(associate);
  const [showUsersListModal, setShowUsersListModal] = useState(false);

  const dispatch = useDispatch();

  const toggleAssociateModal = () => {
    setShowAssociateModal(!showAssociateModal);
  };

  const toggleUsersListModal = () => {
    setShowUsersListModal(!showUsersListModal);
  };

  const handleChange = (e) => {
    setVessel({ ...vessel, [e.target.name]: e.target.value });
  };

  const handleChangeFloat = (e) => {
    const converted = parseFloat(e.target.value);
    setVessel({ ...vessel, [e.target.name]: converted });
  };

  const handleChangeNumber = (e) => {
    const converted = parseInt(e.target.value);
    setVessel({ ...vessel, [e.target.name]: converted });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const publicKey = vessel?.isAssociated
        ? vessel?.vesselPublicKey
        : user?.publicKey;
      const privateKey = vessel?.isAssociated
        ? vessel?.vesselSecretKey
        : sessionStorage.getItem("privateKey");
      const payload = {
        sheetName: encrypt(vessel?.sheetName, publicKey, privateKey),
        vesselName: encrypt(vessel?.vesselName, publicKey, privateKey),
        vesselIMO: encrypt(vessel?.vesselIMO, publicKey, privateKey),
        vesselRegistryPort: encrypt(
          vessel?.vesselRegistryPort,
          publicKey,
          privateKey
        ),
        vesselFlag: encrypt(vessel?.vesselFlag, publicKey, privateKey),
        vesselType: encrypt(vessel?.vesselType, publicKey, privateKey),
        vesselLOA: encrypt(`${vessel?.vesselLOA}`, publicKey, privateKey),
        vesselMaxPassengersWithStaterooms: encrypt(
          `${vessel?.vesselMaxPassengersWithStaterooms}`,
          publicKey,
          privateKey
        ),
        vesselDayPassengersOnBoard: encrypt(
          `${vessel?.vesselDayPassengersOnBoard}`,
          publicKey,
          privateKey
        ),
        vesselServiceProvider: encrypt(
          vessel?.vesselServiceProvider,
          publicKey,
          privateKey
        ),
        vesselServiceProviderAddress: encrypt(
          vessel?.vesselServiceProviderAddress,
          publicKey,
          privateKey
        ),
        vesselServiceProviderCompany: encrypt(
          `${vessel?.vesselServiceProviderCompany}`,
          publicKey,
          privateKey
        ),
        vesselServiceProviderInsuranceRef: encrypt(
          vessel?.vesselServiceProviderInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselServiceProviderVAT: encrypt(
          vessel?.vesselServiceProviderVAT,
          publicKey,
          privateKey
        ),
        vesselCentralAgent: encrypt(
          vessel?.vesselCentralAgent,
          publicKey,
          privateKey
        ),
        vesselCentralAgentAddress: encrypt(
          vessel?.vesselCentralAgentAddress,
          publicKey,
          privateKey
        ),
        vesselCentralAgentCompany: encrypt(
          `${vessel?.vesselCentralAgentCompany}`,
          publicKey,
          privateKey
        ),
        vesselCentralAgentInsuranceRef: encrypt(
          vessel?.vesselCentralAgentInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselCentralAgentVAT: encrypt(
          vessel?.vesselCentralAgentVAT,
          publicKey,
          privateKey
        ),
        vesselCentralAgentMYBA: encrypt(
          vessel?.vesselCentralAgentMYBA,
          publicKey,
          privateKey
        ),
        vesselStakeholder: encrypt(
          vessel?.vesselStakeholder,
          publicKey,
          privateKey
        ),
        vesselStakeholderAddress: encrypt(
          vessel?.vesselStakeholderAddress,
          publicKey,
          privateKey
        ),
        vesselStakeholderCompany: encrypt(
          `${vessel?.vesselStakeholderCompany}`,
          publicKey,
          privateKey
        ),
        vesselStakeholderInsuranceRef: encrypt(
          vessel?.vesselStakeholderInsuranceRef,
          publicKey,
          privateKey
        ),
        vesselStakeholderMYBA: encrypt(
          vessel?.vesselStakeholderMYBA,
          publicKey,
          privateKey
        ),
        vesselStakeholderVAT: encrypt(
          vessel?.vesselStakeholderVAT,
          publicKey,
          privateKey
        ),
        vesselStakeholderFinancialGuarantee: encrypt(
          vessel?.vesselStakeholderFinancialGuarantee,
          publicKey,
          privateKey
        ),
        vesselSpecialConditions: encrypt(
          vessel?.vesselSpecialConditions,
          publicKey,
          privateKey
        ),
      };
      const response = await vesselServices.update(payload, id);
      dispatch(alertActions.setAlert(t(response), alertConstants.SUCCESS));

      setIsLoading(false);
    } catch (error) {
      dispatch(alertActions.setAlert(t(error), alertConstants.ERROR));
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    history.push("/vessels");
  };

  return (
    <>
      <Loader isShown={isLoading} />

      {vessel && user && (
        <>
          <AssociateUserVessel
            toggle={toggleAssociateModal}
            show={showAssociateModal}
            isLoading={isLoading}
            vesselId={id}
            publicKey={user?.publicKey}
            privateKey={privateKey}
          />

          <AssociatedUsersList
            vesselId={id}
            sheetName={vessel?.sheetName}
            toggle={toggleUsersListModal}
            show={showUsersListModal}
            toggleAdd={toggleAssociateModal}
            publicKey={user?.publicKey}
            privateKey={privateKey}
            vessel={vessel}
          />
        </>
      )}

      <div className={classes?.formHeader}>
        <h1 className="page-title">{t("Edit vessel")}</h1>
      </div>
      <div className={classes.buttonsGroup}>
        <Button onClick={toggleAssociateModal}>
          <i className="fa fa-user-plus"></i> {t("Associate user")}
        </Button>
        <Button onClick={toggleUsersListModal}>
          <i className="fa fa-anchor"></i> {t("View associated users")}
        </Button>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <FormikForm>
            <Form.Group>
              <Form.Label>
                {t("Name")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Sheet name")}
                required
                name="sheetName"
                maxLength={40}
                value={vessel?.sheetName}
                onChange={handleChange}
                className="form-control"
              />
              <ErrorMessage
                name="sheetName"
                component="div"
                className="text-danger"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Name of the vessel")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Name of the vessel")}
                required
                name="vesselName"
                maxLength={30}
                value={vessel?.vesselName}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("IMO number")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("IMO number")}
                required
                name="vesselIMO"
                maxLength={20}
                value={vessel?.vesselIMO}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Port of registry")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Port of registry")}
                required
                name="vesselRegistryPort"
                maxLength={30}
                value={vessel?.vesselRegistryPort}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Flag")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Flag")}
                required
                name="vesselFlag"
                maxLength={20}
                value={vessel.vesselFlag}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Type")} <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Form.Group>
                <Form.Check
                  type="radio"
                  label="M/Y"
                  value={"M/Y"}
                  required
                  name="vesselType"
                  onChange={handleChange}
                  checked={vessel?.vesselType === "M/Y"}
                />
                <Form.Check
                  type="radio"
                  label={"S/Y"}
                  value={"S/Y"}
                  required
                  name="vesselType"
                  onChange={handleChange}
                  checked={vessel?.vesselType === "S/Y"}
                />{" "}
              </Form.Group>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Length overall")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="number"
                placeholder={t("xxx.xx")}
                required
                name="vesselLOA"
                value={vessel?.vesselLOA}
                onChange={handleChangeFloat}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Maximum number of Passengers with staterooms")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="number"
                placeholder={t("xx")}
                required
                name="vesselMaxPassengersWithStaterooms"
                value={vessel?.vesselMaxPassengersWithStaterooms}
                onChange={handleChangeNumber}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Day Passengers on board")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="number"
                placeholder={t("xx")}
                required
                name="vesselDayPassengersOnBoard"
                value={vessel.vesselDayPassengersOnBoard}
                onChange={handleChangeNumber}
                className="form-control"
              />
            </Form.Group>
            <hr />

            <h1 className="page-subtitle">{t("Service Provider")}</h1>
            <Form.Group>
              <Form.Label>
                {t("Service provider")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider")}
                required
                name="vesselServiceProvider"
                maxLength={40}
                value={vessel?.vesselServiceProvider}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>
                {t("Service provider address")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider address")}
                required
                name="vesselServiceProviderAddress"
                maxLength={150}
                value={vessel?.vesselServiceProviderAddress}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider Company ID")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider CompanyID")}
                required
                name="vesselServiceProviderCompany"
                maxLength={40}
                value={vessel?.vesselServiceProviderCompany}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider insurance reference")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider insurance reference")}
                required
                name="vesselServiceProviderInsuranceRef"
                maxLength={30}
                value={vessel?.vesselServiceProviderInsuranceRef}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t("Service provider VAT number")}{" "}
                <span style={{ color: "#793b2f" }}>*</span>
              </Form.Label>
              <Field
                type="text"
                placeholder={t("Service provider VAT number")}
                required
                name="vesselServiceProviderVAT"
                maxLength={20}
                value={vessel?.vesselServiceProviderVAT}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <hr />

            <h1 className="page-subtitle">{t("Central Agent")}</h1>
            <Form.Group>
              <Form.Label>{t("Central agent")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent")}
                name="vesselCentralAgent"
                maxLength={40}
                value={vessel?.vesselCentralAgent}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent address")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent address")}
                name="vesselCentralAgentAddress"
                maxLength={75}
                value={vessel?.vesselCentralAgentAddress}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent Company ID")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent Company ID")}
                name="vesselCentralAgentCompany"
                maxLength={40}
                value={vessel?.vesselCentralAgentCompany}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent insurance reference")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent insurance reference")}
                name="vesselCentralAgentInsuranceRef"
                maxLength={30}
                value={vessel?.vesselCentralAgentInsuranceRef}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent VAT number")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent VAT number")}
                name="vesselCentralAgentVAT"
                maxLength={20}
                value={vessel?.vesselCentralAgentVAT}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Central agent MYBA id")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Central agent MYBA id")}
                name="vesselCentralAgentMYBA"
                maxLength={10}
                value={vessel?.vesselCentralAgentMYBA}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <hr />
            <h1 className="page-subtitle">{t("Stakeholder")}</h1>
            <Form.Group>
              <Form.Label>{t("Stakeholder")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder")}
                name="vesselStakeholder"
                maxLength={40}
                value={vessel?.vesselStakeholder}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder address")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder address")}
                name="vesselStakeholderAddress"
                maxLength={75}
                value={vessel?.vesselStakeholderAddress}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder Company ID")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder Company ID")}
                name="vesselStakeholderCompany"
                maxLength={40}
                value={vessel?.vesselStakeholderCompany}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder insurance reference")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder insurance reference")}
                name="vesselStakeholderInsuranceRef"
                maxLength={30}
                value={vessel?.vesselStakeholderInsuranceRef}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder VAT number")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder VAT number")}
                name="vesselStakeholderVAT"
                maxLength={20}
                value={vessel?.vesselStakeholderVAT}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder MYBA id")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder MYBA id")}
                name="vesselStakeholderMYBA"
                maxLength={10}
                value={vessel?.vesselStakeholderMYBA}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Stakeholder financial guarantee")}</Form.Label>
              <Field
                type="text"
                placeholder={t("Stakeholder financial guarantee")}
                name="vesselStakeholderFinancialGuarantee"
                maxLength={30}
                value={vessel?.vesselStakeholderFinancialGuarantee}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("Special conditions")}</Form.Label>
              <Field
                as="textarea"
                type="text"
                name="vesselSpecialConditions"
                rows={50}
                value={vessel?.vesselSpecialConditions}
                onChange={handleChange}
                className="form-control"
              />
            </Form.Group>
            <div className={classes.formButtonGroup}>
              <Button variant="primary" onClick={handleSubmit}>
                {t("Save")}
              </Button>
              <Button variant="primary" onClick={handleCancel}>
                {t("Cancel")}
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </>
  );
}
